import { Link } from '@tanstack/react-router';
import clsx from 'clsx';

import type { RegisteredRouter, ValidateLinkOptions } from '@tanstack/react-router';
import type { ReactNode } from 'react';

const linkClass =
	'flex w-full items-center gap-2 rounded px-2 py-1 transition-colors hover:bg-gray-1/75 data-[status="active"]:bg-gray-a3 data-[status="active"]:font-semibold';

// Type signature is a bit obtuse but it's required for TanStack Router type-safety:
// https://tanstack.com/router/latest/docs/framework/react/guide/type-utilities#type-checking-link-options-with-validatelinkoptions
export type SidebarLinkProps<
	TRouter extends RegisteredRouter = RegisteredRouter,
	TOptions = unknown,
> = ValidateLinkOptions<TRouter, TOptions> & {
	icon: ReactNode;
	label: string;
	/** Overrides the router's own active-route detection when provided. */
	active?: boolean;
};

/**
 * A navigation link in the sidebar, with an icon, a label, and active-route highlighting.
 */
export function SidebarLink<TRouter extends RegisteredRouter, TOptions>(
	props: SidebarLinkProps<TRouter, TOptions>,
): ReactNode;
export function SidebarLink({ icon, label, active: activeOverride, ...linkOptions }: SidebarLinkProps) {
	return (
		<Link {...linkOptions} className={linkClass} data-status={activeOverride ? 'active' : undefined}>
			{(state) => {
				const isActive = activeOverride ?? state.isActive;
				return (
					<>
						<span
							className={clsx(
								'flex shrink-0 items-center transition-colors',
								isActive ? 'text-blue-11' : 'text-gray-10',
							)}
						>
							{icon}
						</span>
						<div className={clsx(isActive && 'text-blue-11')}>{label}</div>
					</>
				);
			}}
		</Link>
	);
}
